import type { Language } from '@eformulieren/engine'
import { type ReactNode, useState } from 'react'
import { type ShareSection, type WriteBlock, sectionLabel } from './shareModel.js'
import type { Bilingual } from '../language/text.js'

/**
 * "Welke secties?": een lijst met vinkjes en per gekozen sectie Lezen of Schrijven (besluit 1 van
 * 30 oktober 2026, R-65, R-65a, R-65b, R-65c, R-56, R-140, mockup 01 van delen).
 *
 * Drie standen. Bij meelezen alleen vinkjes, want alles is lezen. Bij meeschrijven per gekozen sectie
 * Lezen of Schrijven; waar schrijven niet kan staat de reden bij de sectie en is er geen keuze, zodat
 * de deler het ziet vóór het versturen en niet als fout achteraf. Bij advies alleen vinkjes, en een
 * sectie die de inrichter van advies uitsloot is niet te kiezen, met die reden erbij.
 *
 * **Eén aantal voor dezelfde verzameling** (besluit van de productowner van 2 november 2026). De teller
 * ("1 van 12 gekozen"), Alles kiezen en "Alle 12 secties" in de dialoog tellen dezelfde secties: wat nu
 * in het formulier staat. Een sectie die een regel nu verbergt telt daar niet in mee, maar blijft te
 * kiezen, in een eigen groep "Nu verborgen in het formulier" met de reden erbij, achter een knop. Te
 * kiezen blijft ze omdat een deling haar rechten bij het uitnodigen vastlegt: toont de regel de sectie
 * later, dan kan de ander er alleen in als ze nu is meegegeven. Is er een gekozen, of noemt een
 * weigering er een, dan staat de groep open: wat er wordt gedeeld, staat in beeld. Is elke sectie nu
 * verborgen, dan is er geen aparte groep en telt de lijst ze gewoon.
 *
 * **Alles kiezen en Niets kiezen** staan naast de teller (opdracht van 2 november 2026): één knop die
 * zegt wat hij doet. Alles kiezen vinkt elke sectie aan die nu in de lijst staat en te kiezen is, met
 * de standaard van `defaultPermission`, dus nooit meer dan de deler zelf kan geven (R-65a); een sectie
 * die van advies is uitgesloten blijft uit. Wat al gekozen was houdt zijn recht.
 *
 * Een fout bij de secties hoort bij de groep, en bij elke sectie die de pod noemde (R-140): het vinkje
 * krijgt `aria-invalid` en de melding in zijn beschrijving.
 */

export type PickerMode = 'lezen' | 'schrijven' | 'advies'

export interface PickerError {
  readonly id: string
  readonly tekst: string
  /** De secties waar de melding over gaat; leeg als ze over de keuze als geheel gaat. */
  readonly secties: readonly string[]
}

export interface SectionPickerProps {
  readonly taal: Language
  /** Een unieke basis voor de id's binnen de dialoog. */
  readonly idBase: string
  readonly legend: string
  /** De legenda alleen voor de schermlezer, als er al een zichtbare keuze boven staat die de lijst noemt. */
  readonly legendVoorlezen?: boolean
  readonly mode: PickerMode
  readonly secties: readonly ShareSection[]
  readonly value: Readonly<Record<string, 'lezen' | 'bewerken'>>
  readonly onChange: (next: Readonly<Record<string, 'lezen' | 'bewerken'>>) => void
  readonly fout?: PickerError
}

const T = {
  gekozen: { nl: '{n} van {m} gekozen.', en: '{n} of {m} chosen.' },
  allesKiezen: { nl: 'Alles kiezen', en: 'Select all' },
  nietsKiezen: { nl: 'Niets kiezen', en: 'Select none' },
  perSectie: { nl: 'U kunt per sectie ook alleen meelezen geven.', en: 'Per section you can also give read access only.' },
  lezen: { nl: 'Lezen', en: 'Read' },
  schrijven: { nl: 'Schrijven', en: 'Write' },
  rechtIn: { nl: 'Recht in {s}', en: 'Access to {s}' },
  // Het aantal in de zin en niet tussen haakjes erachter (besluit van 1 november 2026).
  toonEen: { nl: 'Toon de verborgen sectie', en: 'Show the hidden section' },
  toonMeer: { nl: 'Toon de {n} verborgen secties', en: 'Show the {n} hidden sections' },
  minder: { nl: 'Verborgen secties niet tonen', en: 'Do not show hidden sections' },
  verborgenKop: { nl: 'Nu verborgen in het formulier', en: 'Currently hidden in the form' },
  verborgenWaarom: {
    nl: 'Een regel in het formulier verbergt ze nu. Delen kan toch; de ander ziet ze pas als de regel ze toont.',
    en: 'A rule in the form hides them now. You can still share them; the other person sees them once the rule shows them.',
  },
  uitgesloten: { nl: 'Deze sectie is in dit formulier uitgesloten van advies.', en: 'This section is excluded from advice in this form.' },
} as const satisfies Readonly<Record<string, Bilingual>>

const REASON: Readonly<Record<WriteBlock, Bilingual>> = {
  toetssectie: { nl: 'Alleen lezen: een toetssectie deelt u alleen om te lezen.', en: 'Read only: a review section can only be shared for reading.' },
  bronsectie: {
    nl: 'Alleen lezen: gegevens uit een eerder formulier, daar schrijft niemand.',
    en: 'Read only: data from an earlier form, nobody writes there.',
  },
  toetser: { nl: 'Alleen lezen: u toetst dit formulier, en een toetser geeft geen schrijfrecht.', en: 'Read only: you review this form, and a reviewer does not give write access.' },
  'zelf-lezen': { nl: 'Alleen lezen: u leest deze sectie zelf alleen.', en: 'Read only: you can only read this section yourself.' },
}

/** De standaard voor een sectie die net wordt aangevinkt. */
export function defaultPermission(section: ShareSection, mode: PickerMode): 'lezen' | 'bewerken' {
  return mode === 'schrijven' && section.magBewerken ? 'bewerken' : 'lezen'
}

export function SectionPicker({ taal: language, idBase, legend, legendVoorlezen: legendOnlyRead = false, mode, secties: sections, value, onChange, fout: error }: SectionPickerProps): ReactNode {
  const [showHidden, setShowHidden] = useState(false)
  const visible = sections.filter((section) => section.verborgen !== true)
  // Is alles nu verborgen, dan is er niets om apart te zetten: dan telt de lijst ze gewoon.
  const separate = visible.length > 0
  const counted = separate ? visible : sections
  const hidden = separate ? sections.filter((section) => section.verborgen === true) : []
  const hintId = `${idBase}-hint`
  const hiddenHeadId = `${idBase}-verborgen-kop`
  const hiddenWhyId = `${idBase}-verborgen-waarom`
  const chosen = counted.filter((section) => value[section.id] !== undefined).length
  const hint = `${T.gekozen[language].replace('{n}', String(chosen)).replace('{m}', String(counted.length))}${mode === 'schrijven' ? ` ${T.perSectie[language]}` : ''}`
  const groupError = error !== undefined && error.secties.length === 0
  const choosable = counted.filter((section) => !(mode === 'advies' && section.adviesUitgesloten === true))
  const allChosen = choosable.length > 0 && choosable.every((section) => value[section.id] !== undefined)
  const hiddenPinned = hidden.some((section) => value[section.id] !== undefined || (error?.secties.includes(section.id) ?? false))
  const hiddenOpen = showHidden || hiddenPinned

  function chooseAll(): void {
    const next = { ...value }
    for (const section of choosable) {
      if (allChosen) delete next[section.id]
      else next[section.id] ??= defaultPermission(section, mode)
    }
    onChange(next)
  }

  function row(section: ShareSection): ReactNode {
    const checkboxId = `${idBase}-${section.id}`
    const reasonId = `${checkboxId}-reden`
    const permission = value[section.id]
    const excluded = mode === 'advies' && section.adviesUitgesloten === true
    const reason = excluded
      ? T.uitgesloten[language]
      : mode === 'schrijven' && !section.magBewerken && section.schrijfReden !== undefined
        ? REASON[section.schrijfReden][language]
        : ''
    const involved = error !== undefined && error.secties.includes(section.id)
    const described = [reason === '' ? undefined : reasonId, involved ? error.id : undefined].filter((part) => part !== undefined).join(' ')
    const label = sectionLabel(section)
    return (
      <li key={section.id} className="ef-sectiekeuze__sectie" data-status={involved ? 'fout' : undefined}>
        <div className="ef-sectiekeuze__vink">
          <input
            id={checkboxId}
            type="checkbox"
            className="ef-sectiekeuze__vakje"
            checked={permission !== undefined}
            disabled={excluded}
            onChange={(event) => {
              const next = { ...value }
              if (event.target.checked) next[section.id] = defaultPermission(section, mode)
              else delete next[section.id]
              onChange(next)
            }}
            {...(described === '' ? {} : { 'aria-describedby': described })}
            {...(involved ? { 'aria-invalid': true } : {})}
          />
          <label htmlFor={checkboxId} className="ef-sectiekeuze__naam">
            {label}
          </label>
        </div>
        {reason === '' ? null : (
          <p id={reasonId} className="ef-sectiekeuze__reden">
            {reason}
          </p>
        )}
        {mode === 'schrijven' && permission !== undefined && section.magBewerken ? (
          <div className="ef-sectiekeuze__recht" role="radiogroup" aria-label={T.rechtIn[language].replace('{s}', label)}>
            {(['lezen', 'bewerken'] as const).map((option) => (
              <label key={option} className="ef-sectiekeuze__optie">
                <input
                  type="radio"
                  name={`${checkboxId}-recht`}
                  value={option}
                  checked={permission === option}
                  onChange={() => {
                    onChange({ ...value, [section.id]: option })
                  }}
                />
                <span>{option === 'lezen' ? T.lezen[language] : T.schrijven[language]}</span>
              </label>
            ))}
          </div>
        ) : null}
      </li>
    )
  }

  return (
    <fieldset className="ef-sectiekeuze" aria-describedby={groupError ? `${hintId} ${error.id}` : hintId}>
      <legend className={legendOnlyRead ? 'ef-sr-only' : 'ef-veld__label'}>
        <span className="ef-veld__labeltekst">{legend}</span>
      </legend>
      <div className="ef-sectiekeuze__teller">
        <p id={hintId} className="ef-veld__hulptekst">
          {hint}
        </p>
        {choosable.length > 1 ? (
          <button type="button" className="ef-tekstknop ef-sectiekeuze__alles" onClick={chooseAll}>
            {allChosen ? T.nietsKiezen[language] : T.allesKiezen[language]}
          </button>
        ) : null}
      </div>
      <ul className="ef-sectiekeuze__lijst">{counted.map(row)}</ul>
      {hidden.length === 0 ? null : (
        <div className="ef-sectiekeuze__verborgen" role="group" aria-labelledby={hiddenHeadId} aria-describedby={hiddenWhyId}>
          <p id={hiddenHeadId} className="ef-sectiekeuze__groepkop">
            {T.verborgenKop[language]}
          </p>
          <p id={hiddenWhyId} className="ef-sectiekeuze__reden ef-sectiekeuze__groepreden">
            {T.verborgenWaarom[language]}
          </p>
          {hiddenOpen ? <ul className="ef-sectiekeuze__lijst">{hidden.map(row)}</ul> : null}
          {hiddenPinned ? null : (
            <button
              type="button"
              className="ef-tekstknop ef-sectiekeuze__meer"
              aria-expanded={hiddenOpen}
              onClick={() => {
                setShowHidden((previous) => !previous)
              }}
            >
              {hiddenOpen ? T.minder[language] : hidden.length === 1 ? T.toonEen[language] : T.toonMeer[language].replace('{n}', String(hidden.length))}
            </button>
          )}
        </div>
      )}
      {error === undefined ? null : (
        <p id={error.id} role="alert" className="ef-veld__melding--hard">
          {error.tekst}
        </p>
      )}
    </fieldset>
  )
}
